import { useEffect, useRef, useState } from "react";
import { Download, FolderOpen, Link as LinkIcon } from "lucide-react";
import { track } from "../../lib/events";
import { outlineButtonClass } from "../ui";
import { LocalImg, LocalLink } from "../../offline/LocalMedia";
import { usePreloadImages } from "../../lib/preloadImages";
import { useLocalAsset } from "../../offline/useLocalAsset";
import { ManualCompleteButton } from "./ManualCompleteButton";
import { useCompletion } from "./useCompletion";
import { PdfSlides } from "./PdfSlides";
import { useEmbedEvents } from "./useEmbedEvents";
import { useReadDepth } from "./useReadDepth";
import { useViewTrail } from "./useViewTrail";
import { contentOf } from "./types";
import type {
  DownloadContent,
  EmbedContent,
  ImageContent,
  LinkContent,
  RendererProps,
  ResourcePackContent,
  SlidesContent,
  TextContent,
} from "./types";

const EMBED_ALLOWED_ORIGINS: Record<string, string> = {
  youtube: "https://www.youtube.com",
  vimeo: "https://player.vimeo.com",
  google_slides: "https://docs.google.com",
  google_forms: "https://docs.google.com",
  google_drive: "https://drive.google.com",
};

export function TextRenderer({ item, moduleID, onComplete }: RendererProps) {
  const content = contentOf<TextContent>(item);
  const completion = useCompletion(item, moduleID, onComplete);
  const body = useRef<HTMLDivElement | null>(null);
  useReadDepth(body, item, moduleID, Boolean(content.body));
  // body is sanitized server-side (bluemonday) before storage.
  return (
    <section className="grid gap-4">
      <div
        ref={body}
        className="text-base leading-relaxed text-app-ink"
        dangerouslySetInnerHTML={{ __html: content.body || "" }}
      />
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}

export function ImageRenderer({ item, moduleID, onComplete }: RendererProps) {
  const images = contentOf<ImageContent>(item).images || [];
  const completion = useCompletion(item, moduleID, onComplete);
  return (
    <section className="grid gap-4">
      <div className="grid gap-3">
        {images.map((image, index) => (
          <figure key={image.id || index} className="m-0">
            <LocalImg
              className="w-full rounded-md bg-app-field"
              src={image.url}
              alt={image.alt_text || image.caption || "Learning material"}
              loading="lazy"
            />
            {image.caption && (
              <figcaption className="mt-1 text-sm text-app-copy">{image.caption}</figcaption>
            )}
          </figure>
        ))}
      </div>
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}

export function GalleryRenderer({ item, moduleID, onComplete }: RendererProps) {
  const images = contentOf<ImageContent>(item).images || [];
  const [index, setIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const completion = useCompletion(item, moduleID, onComplete);
  const viewed = useViewTrail(item, moduleID, "gallery_image_viewed", images.length);
  usePreloadImages(images.map((image) => image.url));
  const current = images[index];

  useEffect(() => {
    if (images.length > 0) viewed(0, "open");
    // Only when the item opens: later changes are reported where they happen.
  }, [item.id]);

  function go(next: number, by: "next" | "previous") {
    setIndex(next);
    viewed(next, by);
  }
  function setFullscreenAt(open: boolean) {
    setFullscreen(open);
    track("gallery_fullscreen", {
      module_id: moduleID,
      item_id: item.id,
      payload: { index, item_count: images.length, opened: open },
    });
  }
  return (
    <section className="grid gap-4">
      {current && (
        <figure className="m-0">
          <button
            type="button"
            className="block w-full"
            onClick={() => setFullscreenAt(true)}
            aria-label="View full screen"
          >
            <LocalImg
              className="w-full rounded-md bg-app-field"
              src={current.url}
              alt={current.alt_text || current.caption || "Gallery image"}
              loading="lazy"
            />
          </button>
          {current.caption && (
            <figcaption className="mt-1 text-sm text-app-copy">{current.caption}</figcaption>
          )}
        </figure>
      )}
      <div
        className="flex items-center justify-between gap-3"
        role="group"
        aria-label="Gallery navigation"
      >
        <button
          type="button"
          className={outlineButtonClass}
          disabled={index === 0}
          onClick={() => go(Math.max(0, index - 1), "previous")}
        >
          Previous
        </button>
        <span className="text-sm text-app-copy">
          {index + 1} / {images.length}
        </span>
        <button
          type="button"
          className={outlineButtonClass}
          disabled={index >= images.length - 1}
          onClick={() => go(Math.min(images.length - 1, index + 1), "next")}
        >
          Next
        </button>
      </div>
      {fullscreen && current && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/90 p-4"
          onClick={() => setFullscreenAt(false)}
          role="dialog"
          aria-modal="true"
        >
          <LocalImg
            src={current.url}
            alt={current.alt_text || current.caption || ""}
            className="max-h-full max-w-full rounded-md"
          />
        </div>
      )}
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}

export function SlidesRenderer({ item, moduleID, onComplete }: RendererProps) {
  const content = contentOf<SlidesContent>(item);
  const completion = useCompletion(item, moduleID, onComplete);
  const localFile = useLocalAsset(content.file_url);
  const isPdf = content.file_type === "pdf";
  // PDFs are drawn by the app so page views can be recorded; if that cannot
  // work (the viewer code could not be loaded, a damaged file) the browser's own
  // viewer takes over, as before, and the fallback is recorded.
  const [pdfFailed, setPdfFailed] = useState(false);
  const viewerURL = isPdf
    ? localFile
    : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(content.file_url || "")}`;
  return (
    <section className="grid gap-4">
      {content.title && <h3 className="text-base font-semibold text-app-ink">{content.title}</h3>}
      {content.description && <p className="text-sm text-app-copy">{content.description}</p>}
      {isPdf && !pdfFailed ? (
        localFile && (
          <PdfSlides
            item={item}
            moduleID={moduleID}
            src={localFile}
            title={content.title || "Slides"}
            onUnavailable={(reason) => {
              setPdfFailed(true);
              track("slides_viewer_fallback", {
                module_id: moduleID,
                item_id: item.id,
                payload: { reason },
              });
            }}
          />
        )
      ) : (
        <div className="aspect-[4/3] w-full overflow-hidden rounded-md border border-app-line bg-app-field">
          <iframe className="h-full w-full" src={viewerURL} title={content.title || "Slides"} />
        </div>
      )}
      <LocalLink
        className="flex items-center justify-center gap-2 rounded-md border border-app-line p-3 text-sm font-medium text-accent hover:bg-app-field"
        href={content.file_url}
        target="_blank"
        rel="noreferrer"
        fileName={content.title}
        onClick={() => {
          track("download_requested", {
            module_id: moduleID,
            item_id: item.id,
            payload: { file: content.title },
          });
          completion.reportDownload();
        }}
      >
        <Download size={16} />
        Open or download if the preview doesn&apos;t load
      </LocalLink>
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}

export function DownloadRenderer({ item, moduleID, onComplete }: RendererProps) {
  const content = contentOf<DownloadContent>(item);
  const completion = useCompletion(item, moduleID, onComplete);
  return (
    <section className="grid gap-4">
      {content.description && <p className="text-sm text-app-copy">{content.description}</p>}
      <LocalLink
        className="flex items-center gap-2 rounded-md border border-app-line p-4 font-medium text-accent hover:bg-app-field"
        href={content.file_url}
        target="_blank"
        rel="noreferrer"
        fileName={content.file_name}
        onClick={() => {
          track("download_requested", {
            module_id: moduleID,
            item_id: item.id,
            payload: { file: content.file_name },
          });
          completion.reportDownload();
        }}
      >
        <Download size={18} />
        Download {content.file_name}
      </LocalLink>
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}

export function ResourcePackRenderer({ item, moduleID, onComplete }: RendererProps) {
  const content = contentOf<ResourcePackContent>(item);
  const files = content.files || [];
  const completion = useCompletion(item, moduleID, onComplete);
  return (
    <section className="grid gap-4">
      {content.description && <p className="text-sm text-app-copy">{content.description}</p>}
      <div className="divide-y divide-app-line rounded-md border border-app-line">
        {files.map((file) => (
          <LocalLink
            key={file.id || file.file_name}
            className="flex items-center gap-3 p-3 text-sm font-medium text-app-ink hover:bg-app-field"
            href={file.file_url}
            target="_blank"
            rel="noreferrer"
            fileName={file.file_name}
            onClick={() => {
              track("download_requested", {
                module_id: moduleID,
                item_id: item.id,
                payload: { file: file.file_name },
              });
              completion.reportDownload();
            }}
          >
            <FolderOpen size={16} className="shrink-0 text-app-copy" />
            <span className="min-w-0 flex-1">
              <span className="block truncate">{file.file_name}</span>
              {file.description && (
                <span className="block truncate text-xs text-app-copy">{file.description}</span>
              )}
            </span>
            <Download size={16} className="shrink-0" />
          </LocalLink>
        ))}
      </div>
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}

export function LinkRenderer({ item, moduleID, onComplete }: RendererProps) {
  const content = contentOf<LinkContent>(item);
  const completion = useCompletion(item, moduleID, onComplete);
  return (
    <section className="grid gap-4">
      {content.thumbnail_url && (
        <LocalImg src={content.thumbnail_url} alt="" className="w-full rounded-md" />
      )}
      {content.description && <p className="text-sm text-app-copy">{content.description}</p>}
      <a
        className="flex items-center justify-center gap-2 rounded-md border border-app-line p-4 font-medium text-accent hover:bg-app-field"
        href={content.url}
        target={content.open_in === "current" ? "_self" : "_blank"}
        rel="noopener noreferrer"
        onClick={() => {
          // Opening a link is not completing the item: completion comes from
          // the item's rule, through useCompletion.
          track("link_opened", { module_id: moduleID, item_id: item.id });
          completion.reportResponseSubmitted();
        }}
      >
        <LinkIcon size={16} />
        {content.label}
      </a>
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}

export function EmbedRenderer({ item, moduleID, onComplete }: RendererProps) {
  const content = contentOf<EmbedContent>(item);
  const completion = useCompletion(item, moduleID, onComplete);
  const allowed = Boolean(content.provider && EMBED_ALLOWED_ORIGINS[content.provider]);
  const [ratioWidth, ratioHeight] = (content.aspect_ratio || "16:9").split(":").map(Number);
  const frame = useRef<HTMLIFrameElement | null>(null);
  useEmbedEvents(frame, item, moduleID, content.provider, allowed);
  // YouTube only reports what the viewer does if the player is asked to.
  const src =
    content.provider === "youtube" &&
    content.embed_url &&
    !content.embed_url.includes("enablejsapi")
      ? `${content.embed_url}${content.embed_url.includes("?") ? "&" : "?"}enablejsapi=1`
      : content.embed_url;
  return (
    <section className="grid gap-4">
      {content.title && <h3 className="text-base font-semibold text-app-ink">{content.title}</h3>}
      {allowed ? (
        <div
          className="w-full overflow-hidden rounded-md border border-app-line"
          style={{ aspectRatio: `${ratioWidth} / ${ratioHeight}` }}
        >
          <iframe
            ref={frame}
            className="h-full w-full"
            src={src}
            title={content.title || "Embedded content"}
            sandbox="allow-scripts allow-same-origin allow-forms allow-presentation"
          />
        </div>
      ) : (
        <p className="text-sm text-accent-strong">This embed provider is not allowed to render.</p>
      )}
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}
